import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useOptionalDocumentTitleContext } from '@/contexts/DocumentTitleContext';

export const useDocumentTitle = () => {
  const location = useLocation();
  const titleContext = useOptionalDocumentTitleContext();

  useEffect(() => {
    // Use custom title if available, otherwise use default based on route
    if (titleContext?.customTitle) {
      document.title = titleContext.customTitle;
    } else {
      const title = getPageTitle(location.pathname);
      document.title = title;
    }
  }, [location.pathname, titleContext?.customTitle]);
};

export const getPageTitle = (pathname: string): string => {
  const baseTitle = 'Practist';

  if (pathname.startsWith('/matter/')) {
    return 'Matter';
  }

  const routeTitles: Record<string, string> = {
    '/': baseTitle,
    '/login': 'Login',
    '/register': 'Register',
    '/forgot-password': 'Forgot Password',
    '/matters': 'Matters',
    '/flywheel': 'Flywheel',
    '/fees/time': 'Time',
    '/fees/flat-fees': 'Flat Fees',
    '/fees/insurance': 'Insurance',
    '/expenses': 'Expenses',
    '/tasks': 'Tasks',
    '/biz-dev': 'Business Development',
    '/invoices': 'Invoices',
    '/invoices/list': 'Invoices',
    '/invoices/manual-invoice-entry': 'Manual Invoice Entry',
    '/ops-hr': 'Transactions',
    '/ops-hr/transactions': 'Transactions',
    '/ops-hr/compensation': 'Compensation',
    '/ops-hr/trust-ledger': 'Trust Ledger',
    '/resources': 'Resources',
    '/wellness': 'Wellness',
    '/settings': 'Settings',
    '/settings/admin': 'Admin Settings',
    '/settings/payment-setup': 'Payment Setup',
    '/settings/form-builder': 'Form Builder',
    '/settings/lead-score': 'Lead Score',
    '/settings/financial-accounts': 'Financial Accounts',
    '/settings/reports': 'Reports',
    '/settings/general': 'Settings',
    '/contacts': 'Contacts',
    '/practist-account': 'Account',
    '/my-settings': 'My Settings',
    '/my-settings/communication': 'Communication events',
    '/email-account': 'Email Account',
    '/spreadsheet': 'Spreadsheet',
    '/claims': 'Claims',
    '/findalawyer': 'Find a Lawyer',
    '/privacy': 'Privacy Policy',
  };

  const basePath = pathname.split('/').slice(0, 2).join('/');

  if (pathname.startsWith('/contacts/')) {
    const tab = pathname.split('/')[2];
    const tabTitle = tab
      ? tab.charAt(0).toUpperCase() + tab.slice(1)
      : 'Contacts';
    return tabTitle;
  }

  if (pathname.startsWith('/engagement/')) {
    return 'Sign Engagement Letter';
  }

  return routeTitles[pathname] || routeTitles[basePath] || baseTitle;
};
